import { withUser } from '@digital-retail/falabella-ui-cross-components';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import constants from '../../config/constants';
import { withApplicationContext } from '../../utils/ApplicationContext';
import { withCart } from '../../utils/CartContext';
import { CookieStorage } from '../../utils/CookieStorage';
import { withProduct } from '../../utils/ProductContext';
import { track } from '../analytic/index';
import { ReviewSectionDesktopStyles, ReviewSectionMobileStyles } from './ReviewSection.style';

const ReviewMetricsPanel = dynamic(
  () => import('@digital-retail/rating-and-reviews').then((lib) => lib.ReviewMetricsPanel),
  {
    ssr: false,
    loading: () => '',
  }
);

const Reviews = dynamic(() => import('@digital-retail/rating-and-reviews').then((lib) => lib.Reviews), {
  ssr: false,
  loading: () => '',
});
const TopPhotos = dynamic(() => import('@digital-retail/rating-and-reviews').then((lib) => lib.TopPhotos), {
  ssr: false,
  loading: () => '',
});
const ReviewSummary = dynamic(() => import('@digital-retail/rating-and-reviews').then((lib) => lib.ReviewSummary), {
  ssr: false,
  loading: () => '',
});

const ReviewSection = ({ appCtx }) => {
  const { deviceType } = appCtx;
  const reviewSectionStyles = deviceType === 'desktop' ? ReviewSectionDesktopStyles : ReviewSectionMobileStyles;
  const [isReviewsExpanded, setIsReviewsExpanded] = useState(true);
  const reviewSectionRef = useRef(null);
  const hasTrackedImpression = useRef(false);

  const reviewSectionHeading =
    appCtx?.siteConfig?.textDictionary?.INHOUSE_REVIEW_SECTION_HEADING ?? constants.REVIEW_SECTION_HEADING;
  const reviewShowButtonText =
    appCtx?.siteConfig?.textDictionary?.REVIEW_SHOW_BUTTON_TEXT ?? constants.REVIEW_SHOW_BUTTON_TEXT;
  const starsColor = appCtx?.siteConfig?.configurations?.reviewsTexts?.starsColor ?? '#333';
  const reviewStarsColor = appCtx?.siteConfig?.configurations?.reviewsTexts?.reviewStarsColor ?? '#333';

  const isReviewSummaryEnabled =
    (CookieStorage.getCookie(constants.IS_REVIEW_SUMMARY_ENABLED_COOKIE_KEY) ?? 'true') === 'true';

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedImpression.current) {
          track('Ratings_IA_summary_impression');
          hasTrackedImpression.current = true;
          observer.unobserve(entry.target);
        }
      },
      {
        rootMargin: '0px',
        threshold: 0.1,
      }
    );

    const currentRef = reviewSectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const handleToggleReviews = () => {
    track('ratings_show_all_opinions');
    setIsReviewsExpanded(!isReviewsExpanded);
  };

  const moreReviewsCallback = () => {
    track('ratings_show_more');
  };

  return (
    <div ref={reviewSectionRef} className="review-section-container" id="InHouseReviewsContainer">
      <div className="review-section-heading">{reviewSectionHeading}</div>
      <div className="review-section-top-container">
        <ReviewMetricsPanel starsColor={starsColor} />
      </div>
      <div className="top-photos-pdp">
        <TopPhotos />
      </div>
      {isReviewSummaryEnabled && <ReviewSummary />}
      <div className="reviews-toggle-container">
        <button
          type="button"
          className="reviews-toggle-button"
          onClick={handleToggleReviews}
          aria-expanded={isReviewsExpanded}
        >
          {`${isReviewsExpanded ? 'Ocultar' : 'Mostrar'} ${reviewShowButtonText.toLowerCase()}`}
        </button>
      </div>
      {isReviewsExpanded && (
        <div className="reviews-container">
          <Reviews starsColor={reviewStarsColor} moreReviewsCallback={moreReviewsCallback} />
        </div>
      )}

      <style jsx>{reviewSectionStyles}</style>
    </div>
  );
};

ReviewSection.defaultProps = {
  appCtx: {},
};

ReviewSection.propTypes = {
  appCtx: PropTypes.object,
};

const ReviewSectionWithContext = withApplicationContext(withCart(withUser(withProduct(ReviewSection))));

export { ReviewSectionWithContext as ReviewSection };
